import { Directive, inject, TemplateRef } from '@angular/core';
import type { MibMenuItem } from './menu.models';

/** What a row template receives: the item, and whether it is the selected one. */
export interface MibMenuItemContentContext {
  /** The item the row stands for, `data` included. */
  $implicit: MibMenuItem;
  /** The item's `selected`, as a boolean. */
  selected: boolean;
}

/**
 * Draws the inside of every row of the `<mib-menu>` it is placed in, in place of the icon and the label.
 *
 * ```html
 * <mib-menu [menuItems]="items" (itemSelected)="pick($event)">
 *   <ng-template mibMenuItemContent let-item let-selected="selected">
 *     <span class="size-2 rounded-full" [class]="dotOf(item)"></span>
 *     {{ item.label }}
 *   </ng-template>
 * </mib-menu>
 * ```
 *
 * The menu keeps the row itself: its role, the keyboard, focus, the selected and disabled states,
 * the tooltip and `itemSelected`. The template draws content only — never a button or a link.
 */
@Directive({ selector: 'ng-template[mibMenuItemContent]' })
export class MibMenuItemContentDirective {
  /** The template the menu stamps into each row. */
  readonly template = inject<TemplateRef<MibMenuItemContentContext>>(TemplateRef);

  /** Types `let-item` and `let-selected` in the template. */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Angular's guard signature, read by the compiler only
  static ngTemplateContextGuard(_dir: MibMenuItemContentDirective, ctx: unknown): ctx is MibMenuItemContentContext {
    return true;
  }
}
